import { z } from 'zod';
import { Money } from './money';

/**
 * Договор офлайн-синхронизации — сердце кассы.
 * Касса пишет события локально и шлёт пачкой; сервер идемпотентен
 * по eventId, поэтому повторная отправка всегда безопасна.
 */

const base = {
  eventId: z.string().min(20),        // ULID с кассы
  terminalId: z.string(),
  at: z.string().datetime(),          // момент на кассе
};

export const OrderLineModifier = z.object({
  name: z.string(),
  priceDelta: Money,
  cost: Money.optional(),
  removes: z.object({ productId: z.string(), qty: z.number() }).optional(),
});

export const OrderLine = z.object({
  itemId: z.string(),
  productId: z.string().nullable(),
  name: z.string(),
  qty: z.number().positive(),
  unitPrice: Money,                       // база без модификаторов
  modifiers: z.array(OrderLineModifier).default([]),
  /** курс подачи: 0 — срочно, 1..4 — волны */
  course: z.number().int().min(0).max(4).optional(),
});

export const PaymentLine = z.object({
  method: z.enum(['CASH', 'CARD', 'KASPI_QR']),
  amount: Money,
  tenderedCash: Money.optional(),
  /** чаевые картой сверх чека — только optional: контракт заморожен */
  tip: Money.optional(),
});

export const SyncEvent = z.discriminatedUnion('type', [
  z.object({
    ...base,
    type: z.literal('shift.opened'),
    shiftId: z.string(),
    openedBy: z.string(),
    openingCash: Money,
  }),
  z.object({
    ...base,
    type: z.literal('shift.closed'),
    shiftId: z.string(),
    closingCash: Money,
  }),
  z.object({
    ...base,
    type: z.literal('order.closed'),
    orderId: z.string(),
    shiftId: z.string(),
    number: z.number().int(),
    mode: z.enum(['DINE_IN', 'TAKEAWAY', 'DELIVERY']),
    cashierId: z.string(),
    tableId: z.string().optional(),
    tableName: z.string().optional(),
    openedAt: z.string().datetime(),
    items: z.array(OrderLine).min(1),
    payments: z.array(PaymentLine).min(1),
    discount: Money.default(0),
    /** подпись менеджера, если скидка выше полномочий кассира */
    discountToken: z.string().optional(),
    /** сколько бонусов гость списал этим чеком (тиыны) */
    spendBonus: Money.default(0),
    /** сработавшие акции снапшотом — топливо отчёта «что дала акция» */
    promos: z.array(z.object({ name: z.string(), amount: Money })).max(10).default([]),
    guestId: z.string().optional(),
    guestPhone: z.string().optional(),
    /** кто набрал заказ (официант) — может отличаться от закрывшего */
    openedById: z.string().optional(),
    openedByName: z.string().optional(),
    /** зачтённая предоплата брони: чек покрыт ею частично */
    deposit: z.object({ reservationId: z.string(), amount: Money.positive() }).optional(),
    /** подарочный код другу, напечатанный на чеке (сгенерирован кассой) */
    referralCode: z.object({ code: z.string().min(4).max(12), percent: z.number().int() }).optional(),
    /** погашенный чужой промокод: для отчёта «принесло выручки» */
    promoCode: z.object({ code: z.string().min(4).max(12), amount: Money.min(0) }).optional(),
    /** комментарий ко всему чеку: «день рождения», «жалоба по подаче» */
    comment: z.string().max(120).optional(),
    /**
     * Сбор за обслуживание в тиынах и его процент.
     *
     * Без него сервер считал итог по позициям минус скидка — и чек
     * с обслуживанием не сходился: гость заплатил 8 800, сервер ждал
     * 8 000 и отвергал чек целиком. Деньги получены, записи нет.
     */
    service: z.number().int().min(0).optional(),
    servicePct: z.number().int().min(0).max(100).optional(),
  }),
  z.object({
    ...base,
    /**
     * Снятая позиция с причиной. Отдельное событие, а не поле чека:
     * снимают несколько раз за вечер и по разным причинам, и каждое
     * снятие — самостоятельный факт для отчёта
     */
    type: z.literal('item.voided'),
    orderId: z.string(),
    itemId: z.string(),
    name: z.string().max(120),
    /** цифры для акта реализации: старые кассы могут не прислать */
    qty: z.number().positive().optional(),
    sum: z.number().int().nonnegative().optional(),
    reason: z.enum(['WAITER_MISTAKE', 'GUEST_REFUSED', 'QUALITY', 'DISCOUNT_ERROR', 'OTHER']),
    comment: z.string().max(200).optional(),
  }),
  z.object({
    ...base,
    type: z.literal('order.cancelled'),
    orderId: z.string(),
    reason: z.string().min(1).max(120),
    /** имя менеджера, подтвердившего PIN-ом — снапшот для аудита */
    approver: z.string().min(1),
    /** подпись сервера из /auth/elevate — без неё возврат недоказан */
    approverToken: z.string().optional(),
    /**
     * Вернуть заказ официанту вместо простого закрытия.
     * Ошибка по товару: гость не отказался от ужина, ему нужно
     * исправить состав. Без этого официант набирал бы всё заново
     */
    reopen: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('cash.op'),
    eventId: z.string(),
    terminalId: z.string(),
    at: z.string().datetime(),
    shiftId: z.string(),
    kind: z.enum(['PAY_IN', 'PAY_OUT']),
    amount: Money.positive(),
    comment: z.string().max(120).optional(),
    byName: z.string(),
  }),
]);
export type SyncEvent = z.infer<typeof SyncEvent>;

export const SyncRequest = z.object({ events: z.array(SyncEvent).min(1).max(500) });
export type SyncRequest = z.infer<typeof SyncRequest>;

export const SyncResponse = z.object({
  accepted: z.array(z.string()),      // eventId, записанные сейчас
  duplicates: z.array(z.string()),    // уже были — это нормально
  rejected: z.array(z.object({ eventId: z.string(), reason: z.string() })),
});
export type SyncResponse = z.infer<typeof SyncResponse>;
